import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useSpokenLanguagesBuilder } from '@hooks/form-helpers/useSpokenLanguagesBuilder';
import { CustomPillTopBarProps } from '@type/filters';
import {
  Chip, ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { Button, Input } from '@components/common/Forms';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { filterStyles } from '../helpers/filterStyles';


/**
 * @description The top bar spoken languages filter component.
 * ***submitFunc** - the function to trigger the form submit.
 * ***recordFunc** - the 'record' function from useBjForm.
 * ***clearGroup** - the function to clear the group. Defaults to undefined.
 * ***idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 */
export const TopBarLanguages = (props: CustomPillTopBarProps) => {
  // Destructure props
  const {
    submitFunc,
    recordFunc,
    clearGroup,
    idPrefix,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // show/hide the component
  const [hidden, setHidden] = useState(false);

  // Build the spoken languages
  // **********************************************
  const {
    selectedLanguages,
    selectedLanguagesString,
    selectedLanguagesFiltersLabel,
    buildSelectedLanguagesFromString,
    deleteLanguage,
  } = useSpokenLanguagesBuilder();

  // Handle the clear group
  const clearFilterGroup = () => {
    setHidden(true);
    if (clearGroup) clearGroup();
    if (submitFunc) setTimeout(() => submitFunc(), 50);
  };

  // Handle the delete language
  const removeLanguage = (languageId: string) => {
    deleteLanguage(languageId);
    if (submitFunc) setTimeout(() => submitFunc(), 50);
  };


  // Render component
  // **********************************************
  return (
    <>
      <ListboxStyled
        className={`relative${hidden || selectedLanguages.length === 0 ? ' hidden' : ''}`}
        value={selectedLanguages}
        by="name"
        multiple
      >
        {({ open }) => (
          <>
            <ListboxStyledButton
              as={Chip}
              onDelete={clearFilterGroup}
              isDropdownOpen={open}
              withDropdownArrow
              dropdownArrowPosition="left"
            >
              { /* Button label */
                selectedLanguages.length > 1
                  ? `${t('global.filter.spoken_lang')}: ${selectedLanguages.length} ${t('label.selected.multiple')}`
                  : `${selectedLanguages[0]?.name} - ${selectedLanguages[0]?.levelName || ''}`
              }
            </ListboxStyledButton>

            {/* Listbox options */}
            <ListboxStyledOptions
              className={`${filterStyles.listboxOptionsWrapper} py-2`}
              animation="slideDown"
              anchor="bottom start"
            >
              { /* List of selected languages */
                selectedLanguages.map((language) => (
                  <ListboxStyledOption
                    key={language.id}
                    value={language}
                    className={`${filterStyles.listboxOption} justify-between !py-1 !pl-3`}
                  >
                    <span>{`${language.name} - ${language?.levelName || ''}`}</span>
                    <Button
                      styling="none"
                      className="group py-1 pl-2.5"
                      onClick={() => removeLanguage(language.id)}
                    >
                      <span className={filterStyles.closeButtonIcon}>
                        <XMarkIcon className="size-4" />
                      </span>
                    </Button>
                  </ListboxStyledOption>
                ))
              }
            </ListboxStyledOptions>
          </>
        )}
      </ListboxStyled>

      {/* Input that collects the filter languages; use the 'data-filters-label' attribute when dealing with variable filter labels */}
      <Input
        type="hidden"
        value={selectedLanguagesString}
        data-filters-label={selectedLanguagesFiltersLabel}
        onInput={(e) => {
          // If the event is not trusted (e.isTrusted = false), it means it was triggered programmatically
          // (ie: bjForm's setFormValues() function)
          if (!e.isTrusted) buildSelectedLanguagesFromString(e);
        }}
        {...recordFunc('spokenLanguages', {
          id: `${idPrefix || ''}spokenLanguages`,
          convertValue: 'commaStringToArray',
        })}
      />
    </>
  );
};
